/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext } from 'react';
import ArrowBackIcon from '../../assets/icons/arrow-back.svg?react';

interface FilterPanelContextValue {
  /** The second level of the panel. A field renders its options into it. */
  detailHost: HTMLElement | null;
  /** Slides the second level in (true) or back out (false). */
  setDetailActive: (active: boolean) => void;
  /** Closes the whole panel. */
  close: () => void;
  /** How many results the current filters give, when the page knows it. */
  resultCount?: number;
}

export const FilterPanelContext = createContext<FilterPanelContextValue | null>(
  null
);

/** The filter panel a field sits in, or null outside one. */
export function useFilterPanel() {
  return useContext(FilterPanelContext);
}

interface FilterPanelHeaderProps {
  title: string;
  onBack: () => void;
  backLabel: string;
}

/** The blue app bar at the top of both panel levels. */
export function FilterPanelHeader({
  title,
  onBack,
  backLabel,
}: FilterPanelHeaderProps) {
  return (
    <div
      className='from-surface-focus/75 to-surface-focus flex
        min-h-(--spacing-app-bar-height) shrink-0 items-center gap-3
        bg-linear-68 px-4 py-3'
    >
      <button
        type='button'
        onClick={onBack}
        aria-label={backLabel}
        className='text-inverted hover:bg-inverted/10 flex size-10.5 shrink-0
          cursor-pointer items-center justify-center rounded'
      >
        <ArrowBackIcon aria-hidden className='h-6 w-6' />
      </button>
      <h2 className='size-h4 text-inverted min-w-0 truncate font-bold'>
        {title}
      </h2>
    </div>
  );
}

interface FilterPanelFooterProps {
  resetLabel: string;
  onReset?: () => void;
}

/** "See results" and a reset link, at the foot of both panel levels. */
export function FilterPanelFooter({
  resetLabel,
  onReset,
}: FilterPanelFooterProps) {
  const panel = useFilterPanel();

  return (
    <div
      className='border-border-primary flex shrink-0 flex-col gap-2.5 border-t
        px-4 pt-4 pb-8'
    >
      <button
        type='button'
        onClick={panel?.close}
        className='bg-surface-focus hover:bg-surface-focus-strong size-p2
          cursor-pointer rounded-lg px-3 py-3 font-medium text-white
          transition-colors'
      >
        See results
        {panel?.resultCount !== undefined && ` (${panel.resultCount})`}
      </button>
      <button
        type='button'
        onClick={onReset}
        className='size-p2 text-primary cursor-pointer px-3 py-3 underline'
      >
        {resetLabel}
      </button>
    </div>
  );
}
